/**
 * AI CONTEXT: admin-user-management - Reusable user creation modal
 * Journal: J-20250817-01
 * 
 * This component provides a reusable modal for creating new users
 * with auto-verification and password generation.
 * 
 * Features:
 * - Complete user profile creation
 * - Auto-verification of email
 * - Temporary password generation
 * - Address management
 * - Form validation
 * 
 * Usage:
 * - Admin panel user management
 * - Private entry creation
 * - Bulk user creation
 */

import React, { useState } from 'react';
import { UserPlus, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';

const UserCreationModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  title = "Create New User",
  submitText = "Create User",
  loading = false,
  initialForm = {
    name: '',
    email: '',
    phone: '',
    birthday: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    }
  }
}) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!form.birthday) {
      newErrors.birthday = 'Birthday is required';
    }
    
    if (!form.address.line1.trim()) {
      newErrors.line1 = 'Address line 1 is required';
    }
    
    if (!form.address.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!form.address.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!form.address.postalCode.trim()) {
      newErrors.postalCode = 'ZIP code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(form);
    } else {
      toast.error('Please fix the form errors');
    }
  };

  const handleClose = () => {
    setForm(initialForm);
    setErrors({});
    onClose();
  };

  const updateForm = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const updateAddress = (field, value) => {
    setForm(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="glass-card-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
      >
        <div
          className="p-6 max-h-[80vh] overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <UserPlus className="h-6 w-6 text-theme-bronze" />
              <h3 className="text-xl font-bold text-theme-silver">{title}</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-theme-silver-light hover:text-theme-silver"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-theme-silver">Personal Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.name ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter full name"
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.email ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.phone ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter phone number"
                  />
                  {errors.phone && (
                    <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    Birthday *
                  </label>
                  <input
                    type="date"
                    value={form.birthday}
                    onChange={(e) => updateForm('birthday', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.birthday ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                  />
                  {errors.birthday && (
                    <p className="text-red-400 text-sm mt-1">{errors.birthday}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-theme-silver">Shipping Address</h4>
              
              <div>
                <label className="block text-sm font-medium text-theme-silver mb-2">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  value={form.address.line1}
                  onChange={(e) => updateAddress('line1', e.target.value)}
                  className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                    errors.line1 ? 'border-red-500' : 'border-theme-silver/20'
                  }`}
                  placeholder="Enter street address"
                />
                {errors.line1 && (
                  <p className="text-red-400 text-sm mt-1">{errors.line1}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-silver mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={form.address.line2}
                  onChange={(e) => updateAddress('line2', e.target.value)}
                  className="w-full px-3 py-2 bg-theme-black border border-theme-silver/20 rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze"
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={form.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.city ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter city"
                  />
                  {errors.city && (
                    <p className="text-red-400 text-sm mt-1">{errors.city}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={form.address.state}
                    onChange={(e) => updateAddress('state', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.state ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter state"
                  />
                  {errors.state && (
                    <p className="text-red-400 text-sm mt-1">{errors.state}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-theme-silver mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={form.address.postalCode}
                    onChange={(e) => updateAddress('postalCode', e.target.value)}
                    className={`w-full px-3 py-2 bg-theme-black border rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze ${
                      errors.postalCode ? 'border-red-500' : 'border-theme-silver/20'
                    }`}
                    placeholder="Enter ZIP code"
                  />
                  {errors.postalCode && (
                    <p className="text-red-400 text-sm mt-1">{errors.postalCode}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-silver mb-2">
                  Country *
                </label>
                <select
                  value={form.address.country}
                  onChange={(e) => updateAddress('country', e.target.value)}
                  className="w-full px-3 py-2 bg-theme-black border border-theme-silver/20 rounded-lg text-theme-silver focus:border-theme-bronze focus:ring-1 focus:ring-theme-bronze"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    {submitText}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserCreationModal;
